import { Link } from 'react-router-dom';
import { Gavel, Target, TrendingUp, type LucideIcon } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useAlerts, useMnaOverview } from '../lib/api';
import type { AlertDto, WatchlistStage } from '../types';
import { DeltaBadge, EventKindBadge, ScorePair, SourceLink, WATCHLIST_LABELS, fmtDate } from './_shared';

// Tab 1 — Overview. Alerts strip (Phase C — in-app only, no dismiss/ack in v1) + KPI
// tiles + watchlist funnel + top movers + the recent events feed. All aggregates are
// computed by MNA.Api (fixture-derived in Phase A), never stored.

const WATCHLIST_ORDER: WatchlistStage[] = ['radar', 'watch', 'qualified', 'engaged', 'passed'];

const MAX_ALERTS_SHOWN = 5;

const ALERT_ICONS: Record<AlertDto['kind'], LucideIcon> = {
  formal_insolvency: Gavel,
  availability_jump: TrendingUp,
  score_threshold: Target,
};

/** Compact "3h ago" / "2d ago" label for the alert strip. */
function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function Overview() {
  const { data: overview, isLoading, error } = useMnaOverview();
  // Alerts are additive — the tab never blocks (or errors) on them.
  const { data: alerts } = useAlerts();

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error || !overview) return <ErrorState message={(error as Error | null)?.message} />;

  const kpiTiles = [
    { label: 'Universe', value: overview.universeSize },
    { label: 'Scored companies', value: overview.scoredCompanies },
    { label: 'Active distress processes', value: overview.activeDistressProcesses },
    { label: 'Events (7 days)', value: overview.events7d },
  ];

  return (
    <div className="stagger">
      {alerts && alerts.length > 0 && <AlertsStrip alerts={alerts} />}

      <Card>
        <CardTitle>M&amp;A Radar</CardTitle>
        <CardDescription className="mt-2">
          Global acquisition targets and distress signals across the drilling-equipment value
          chain. Every fact carries its source; scores cite the facts behind them.
        </CardDescription>
      </Card>

      {/* KPI tile row */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpiTiles.map((t) => (
          <div
            key={t.label}
            className="bg-surface border border-border rounded-(--radius-md) px-4 py-4 shadow-[var(--shadow-sm)]"
          >
            <div className="font-(family-name:--font-heading) text-3xl font-bold text-text [font-variant-numeric:tabular-nums]">
              {t.value}
            </div>
            <div className="mt-1 font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">
              {t.label}
            </div>
          </div>
        ))}
      </div>

      {/* Watchlist funnel */}
      <Card>
        <span className="eyebrow">Watchlist</span>
        <div className="mt-3 flex flex-wrap gap-3">
          {WATCHLIST_ORDER.map((stage) => (
            <div
              key={stage}
              className="flex min-w-24 flex-col rounded-(--radius-md) border border-border bg-surface-elevated px-4 py-3"
            >
              <span className="font-(family-name:--font-heading) text-2xl font-bold text-text [font-variant-numeric:tabular-nums]">
                {overview.watchlistCounts[stage]}
              </span>
              <span className="mt-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">
                {WATCHLIST_LABELS[stage]}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        {/* Top movers */}
        <Card className="mb-0">
          <span className="eyebrow">Top movers (30 days)</span>
          {overview.topMovers.length === 0 ? (
            <p className="mt-3 text-sm text-muted">No score movement yet — movers appear as scores re-run.</p>
          ) : (
            <ol className="mt-3 flex flex-col">
              {overview.topMovers.map((m) => (
                <li key={m.slug} className="border-b border-border last:border-0">
                  <Link
                    to={`/mna/companies/${m.slug}`}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  >
                    <span className="font-medium text-text">{m.name}</span>
                    <span className="ml-auto flex items-center gap-3">
                      <DeltaBadge delta={m.fitDelta30d} label="Fit" />
                      <DeltaBadge delta={m.availabilityDelta30d} label="Availability" />
                      <ScorePair fit={m.fit} availability={m.availability} />
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </Card>

        {/* Recent events */}
        <Card className="mb-0">
          <span className="eyebrow">Recent events</span>
          {overview.recentEvents.length === 0 ? (
            <p className="mt-3 text-sm text-muted">No events yet — the feed fills as signal adapters land.</p>
          ) : (
            <ol className="mt-3 flex flex-col gap-3">
              {overview.recentEvents.map((e) => (
                <li key={`${e.companySlug}-${e.id}`} className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <EventKindBadge kind={e.kind} />
                    <span className="text-xs text-muted [font-variant-numeric:tabular-nums]">{fmtDate(e.eventDate)}</span>
                    {e.source && <SourceLink source={e.source} />}
                  </div>
                  <p className="text-sm text-text">{e.title}</p>
                  <Link
                    to={`/mna/companies/${e.companySlug}`}
                    className="text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  >
                    {e.companyName}
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ---------- alerts strip (Phase C) ---------- */

function AlertsStrip({ alerts }: { alerts: AlertDto[] }) {
  const sorted = [...alerts].sort((a, b) => b.at.localeCompare(a.at));
  const shown = sorted.slice(0, MAX_ALERTS_SHOWN);
  const hidden = sorted.length - shown.length;

  return (
    <div
      role="status"
      aria-label="Alerts"
      className="mb-6 rounded-(--radius-md) border border-accent-warn bg-surface shadow-[var(--shadow-sm)]"
    >
      <ol className="flex flex-col">
        {shown.map((a) => {
          const Icon = ALERT_ICONS[a.kind];
          return (
            <li
              key={a.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-2.5 last:border-0"
            >
              <Icon
                aria-hidden
                className="h-4 w-4 shrink-0 text-accent-warn"
              />
              <span className="text-sm text-text">{a.message}</span>
              <Link
                to={`/mna/companies/${a.companySlug}`}
                className="text-sm font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {a.companyName}
              </Link>
              <span
                title={a.at}
                className="ml-auto whitespace-nowrap font-(family-name:--font-ui) text-xs text-muted [font-variant-numeric:tabular-nums]"
              >
                {timeAgo(a.at)}
              </span>
            </li>
          );
        })}
      </ol>
      {hidden > 0 && (
        <p className="border-t border-border px-4 py-2 font-(family-name:--font-ui) text-xs uppercase tracking-[0.12em] text-muted">
          {hidden} more alert{hidden === 1 ? '' : 's'} not shown
        </p>
      )}
    </div>
  );
}
